import type { CreateItemRequest, PatchStatusRequest } from './queue.interface.js';
import type { ItemStatus, TerminalItemStatus } from './item-status.js';
import { ItemStatus as ItemStatusEnum, TerminalItemStatus as TerminalEnum } from './item-status.js';
import { isBase64 } from '../tenant-config-module/is-base64.js';

/**
 * UUID v4 nach Spec (8-4-4-4-12 mit Variant-Bits). Toleriert Upper/Lower-Case,
 * weil OpenAPI-Conformance auf `format: uuid` ohne Case-Vorgabe schreibt.
 */
const UUID_V4_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const CREATE_ITEM_KEYS = new Set(['absage_id', 'payload', 'base64Token', 'auth_tag']);
const CANCEL_TOKEN_LENGTH = 24;
const AUTH_TAG_LENGTH = 43;
const BASE64URL_NO_PADDING_PATTERN = /^[A-Za-z0-9_-]+$/u;
/**
 * `payload` ist optional (Spec-Erweiterung Pilot-Stack): Praxis-Consumer
 * darf neben dem Status auch eine patient-verschlüsselte Response
 * mitgeben. Erlaubte Keys umfassen also `status` (Pflicht) und `payload`
 * (optional).
 */
const PATCH_STATUS_ALLOWED_KEYS = new Set(['status', 'payload']);

/**
 * Type-Guard für `CreateItemRequest`. Akzeptiert `additionalProperties: false`,
 * UUID-formatiertes `absage_id`, Base64-`payload` mit Mindestlänge 1.
 *
 * Die Maximal-Länge wird NICHT hier geprüft — das ist Aufgabe des Body-Limits
 * im HTTP-Layer (513-Kib-Antwort statt 400 wenn der Body zu groß ist).
 */
export function isCreateItemRequest(candidate: unknown): candidate is CreateItemRequest {
  if (typeof candidate !== 'object' || candidate === null) {
    return false;
  }
  const record = candidate as Record<string, unknown>;
  if (!hasExactKeys(record, CREATE_ITEM_KEYS)) {
    return false;
  }
  return (
    typeof record.absage_id === 'string' &&
    isUuid(record.absage_id) &&
    typeof record.payload === 'string' &&
    record.payload.length > 0 &&
    isBase64(record.payload) &&
    isCancelTokenString(record.base64Token) &&
    isAuthTagString(record.auth_tag)
  );
}

function isCancelTokenString(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length === CANCEL_TOKEN_LENGTH &&
    BASE64URL_NO_PADDING_PATTERN.test(value)
  );
}

function isAuthTagString(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length === AUTH_TAG_LENGTH &&
    BASE64URL_NO_PADDING_PATTERN.test(value)
  );
}

export function isPatchStatusRequest(candidate: unknown): candidate is PatchStatusRequest {
  if (typeof candidate !== 'object' || candidate === null) {
    return false;
  }
  const record = candidate as Record<string, unknown>;
  if (!hasOnlyAllowedKeys(record, PATCH_STATUS_ALLOWED_KEYS)) {
    return false;
  }
  if (typeof record.status !== 'string' || !isTerminalItemStatus(record.status)) {
    return false;
  }
  if (record.payload !== undefined) {
    if (
      typeof record.payload !== 'string' ||
      record.payload.length === 0 ||
      !isBase64(record.payload)
    ) {
      return false;
    }
  }
  return true;
}

export function isUuid(value: string): boolean {
  return UUID_V4_PATTERN.test(value);
}

export function isItemStatus(value: string): value is ItemStatus {
  return (
    value === ItemStatusEnum.Pending ||
    value === ItemStatusEnum.Success ||
    value === ItemStatusEnum.Error
  );
}

export function isTerminalItemStatus(value: string): value is TerminalItemStatus {
  return value === TerminalEnum.Success || value === TerminalEnum.Error;
}

function hasExactKeys(record: Record<string, unknown>, allowed: ReadonlySet<string>): boolean {
  const keys = Object.keys(record);
  if (keys.length !== allowed.size) {
    return false;
  }
  for (const key of keys) {
    if (!allowed.has(key)) {
      return false;
    }
  }
  return true;
}

function hasOnlyAllowedKeys(
  record: Record<string, unknown>,
  allowed: ReadonlySet<string>,
): boolean {
  for (const key of Object.keys(record)) {
    if (!allowed.has(key)) {
      return false;
    }
  }
  return true;
}
